import { SearchEntityType } from '@upright/common/model/search/types';
import { useTimeout } from '@upright/ui/common/generic/hooks/external/use-timeout';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSearchResults, fetchSemanticSearchResults } from '.';
import { SemanticSearchMode } from './types';

// Keep this hook usable to all entity searches
export function useEntitySearchQueries(
  preprocessedSearchInput: string,
  entityTypesToFetch: readonly SearchEntityType[],
  semanticSearchMode: SemanticSearchMode,
  options?: {
    enabled?: boolean;
    onlyPublicEntities?: boolean;
    includeVisibility?: boolean;
  },
) {
  const enabled = options?.enabled;

  const semanticSearchQuery = useQuery({
    queryKey: [
      'entity-semantic-search',
      preprocessedSearchInput,
      entityTypesToFetch,
      semanticSearchMode,
      { onlyPublicEntities: options?.onlyPublicEntities, includeVisibility: options?.includeVisibility },
    ],

    queryFn: async () => {
      return await fetchSemanticSearchResults(
        preprocessedSearchInput,
        entityTypesToFetch,
        options?.onlyPublicEntities,
        options?.includeVisibility,
      );
    },

    enabled: isSemanticSearchQueryEnabled(semanticSearchMode, enabled),
  });

  const isSemanticSearchSlow = useIsSemanticSearchSlow(semanticSearchQuery.isLoading, preprocessedSearchInput);

  const basicSearchQuery = useQuery({
    queryKey: [
      'entity-basic-search',
      preprocessedSearchInput,
      entityTypesToFetch,
      semanticSearchMode,
      { onlyPublicEntities: options?.onlyPublicEntities, includeVisibility: options?.includeVisibility },
    ],

    queryFn: async () => {
      return await fetchSearchResults(
        preprocessedSearchInput,
        entityTypesToFetch,
        options?.onlyPublicEntities,
        options?.includeVisibility,
      );
    },

    enabled: isBasicSearchQueryEnabled(semanticSearchMode, enabled, isSemanticSearchSlow, semanticSearchQuery.isError),
  });
  return { basicSearchQuery, semanticSearchQuery };
}

function useIsSemanticSearchSlow(isSemanticSearchLoading: boolean, preprocessedSearchInput: string) {
  const [isSemanticSearchSlow, setIsSemanticSearchSlow] = useState(false);
  // Will go to true when semantiSearchQuery has been in LOADING state for 500ms
  const timeOutDelay = isSemanticSearchLoading ? 500 : null;
  useTimeout(() => {
    setIsSemanticSearchSlow(true);
  }, timeOutDelay);

  // Will go to false when input string changes
  useEffect(() => {
    setIsSemanticSearchSlow(false);
  }, [preprocessedSearchInput, setIsSemanticSearchSlow]);
  return isSemanticSearchSlow;
}

function isSemanticSearchQueryEnabled(semanticSearchMode: SemanticSearchMode, enabled?: boolean) {
  return (enabled ?? true) && (semanticSearchMode === 'only semantic' || semanticSearchMode === 'combine both');
}

function isBasicSearchQueryEnabled(
  semanticSearchMode: SemanticSearchMode,
  enabled: boolean | undefined,
  isSemanticSearchSlow: boolean,
  isSemanticSearchError: boolean,
) {
  return (
    (enabled ?? true) &&
    (semanticSearchMode === 'none' ||
      semanticSearchMode === 'combine both' ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (semanticSearchMode === 'only semantic' && (isSemanticSearchSlow || isSemanticSearchError)))
  );
}
